import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useContext, useEffect, useRef, useState } from 'react';
import sizes from 'styles/sizes';

import { Typography, useTheme } from '@mui/material';
import { withEnableDisableScroller } from 'components/Scroller/context';
import IconArrow from 'components/buttons/Dropdown/icons/IconArrow';

import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { getBrandUISettings } from 'lib/utilities/Brands';

import Checkbox from 'components/svg/Checkbox';
import { Select as SelectOption } from 'components/svg/Select';
import {
	DISABLE_SCROLL_WAIT,
	DROPDOWN_BACKGROUND,
	DROPDOWN_BUTTON,
	DROPDOWN_CONTAINER,
	DROPDOWN_LIST_ITEM,
	ENABLE_SCROLL_WAIT,
	MODAL_ANIMATION_DURATION,
	SCROLL_INTO_VIEW_DELAY,
} from './constants';
import { Background, Container, DropdownContainer, Item, List, Modal, ModalDropdown, Select } from './styles';

/**
 * Dropdown component
 * Renders a collapsable list of text items with optional images
 *
 * @param {string} className
 * @param {func} onChange - fn to be a called when an option is selected
 * @param {string} label - button text; changes depending on chosen option
 * @param {bool} showImage - Show image, if present in options
 * @param {object} scrollingElement - Parent Scroller's DOM element
 * @param {bool} disabled
 * @param {string} scrollerId - Parent Scroller's id
 * @param {func} enableScroller - Function enabling Scrollera ssociated with scrollerId
 * @param {func} disableScroller - Function disabled Scroller associated with scrollerId
 * @param {array} options
 *   option
 *   @typedef {{
 *    id: (string|number), - unique identifier from the graph
 *    code: string, - unique identifier from the graph
 *    label: string, - option text seen by user
 *    media: ({}) - shape pending, dependant on the graph
 *  }}
 */

function Dropdown(props) {
	const { className, onChange, label, options, disabled, enableScroller, disableScroller, buttonStyleText, checkbox, select, dataTestId } = props;
	const { brand } = useContext(CruiseSearchContext);
	const { uiSettings } = getBrandUISettings(brand);

	const [open, setOpen] = useState(false);
	const [selectedOption, setSelectedOption] = useState(label.toLowerCase());
	const [hasSpaceRight, setHasSpaceRight] = useState(true);
	const backgroundRef = useRef();
	const containerRef = useRef();
	const modalRef = useRef();
	const dropdownContentRef = useRef();
	const timeoutRef = useRef();
	const { palette } = useTheme();

	useEffect(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		if (open) {
			// Check if there's space on the right
			const { left } = modalRef?.current?.getBoundingClientRect() || {};
			setHasSpaceRight(left < sizes.gutter.small);

			// Scroll into view if needed
			const { top, height } = dropdownContentRef.current?.getBoundingClientRect() || {};
			const scrollIntoView = top + height > window.innerHeight;

			if (scrollIntoView) {
				setTimeout(() => {
					dropdownContentRef.current?.scrollIntoView({
						block: 'end',
						behavior: 'smooth',
					});
				}, SCROLL_INTO_VIEW_DELAY);
			}

			timeoutRef.current = setTimeout(() => {
				disableScroller();
			}, DISABLE_SCROLL_WAIT);
		} else {
			setTimeout(() => {
				setHasSpaceRight(false);
			}, MODAL_ANIMATION_DURATION);

			timeoutRef.current = setTimeout(() => {
				enableScroller();
			}, ENABLE_SCROLL_WAIT);
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [open, enableScroller, disableScroller]);

	const toggleDropdown = () => {
		setOpen(!open);
	};

	const closeDropdown = () => {
		setOpen(false);
	};

	const handleSelect = (optionValue) => {
		if (selectedOption === optionValue?.label.toLowerCase()) return;
		onChange(optionValue);
		setSelectedOption(optionValue?.label);
		closeDropdown();
	};

	const containerClass = classnames({
		[className]: !!className,
		open,
		[DROPDOWN_CONTAINER]: true,
	});

	const btnClass = classnames({
		'btn-style-text': buttonStyleText,
		open,
		DROPDOWN_BUTTON,
	});

	useEffect(() => {
		setSelectedOption(label.toLowerCase());
	}, [label]);

	return (
		<>
			<Background className={DROPDOWN_BACKGROUND} onClick={closeDropdown} ref={backgroundRef} open={open} />
			<Container className={containerClass} ref={containerRef}>
				<DropdownContainer data-testid={dataTestId}>
					<Select
						component="button"
						variant="title2"
						palette={palette}
						className={btnClass}
						onClick={toggleDropdown}
						disabled={disabled}
						styles={uiSettings.resultsHeader.labels}
					>
						<p>{label}</p>
						<IconArrow />
					</Select>
					<Modal ref={modalRef} open={open} anchorLeft={hasSpaceRight} lines={options.length}>
						<ModalDropdown ref={dropdownContentRef}>
							<List>
								{options.map((option, i) => {
									const disabled = option.disabled ?? false;
									const key = `dropdown-${option?.label}-${i}`;
									return (
										<Item
											className={DROPDOWN_LIST_ITEM}
											key={key}
											palette={palette}
											onClick={() => {
												if (!disabled) {
													handleSelect(option);
												}
											}}
											disabled={disabled}
											open={open}
											index={i}
										>
											{checkbox && <Checkbox selected={selectedOption === option?.label?.toLowerCase()} />}
											{select && <SelectOption disabled={disabled} selected={selectedOption === option?.label?.toLowerCase()} palette={palette} />}
											<Typography variant="calendar" component="p">
												{option.label}
											</Typography>
										</Item>
									);
								})}
							</List>
						</ModalDropdown>
					</Modal>
				</DropdownContainer>
			</Container>
		</>
	);
}

Dropdown.propTypes = {
	className: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	label: PropTypes.string.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
			code: PropTypes.string,
			label: PropTypes.string.isRequired,
			media: PropTypes.shape({}), // what is this gonna look like?
		}),
	).isRequired,
	showImage: PropTypes.bool,
	scrollingElement: PropTypes.object,
	disabled: PropTypes.bool,
	scrollerId: PropTypes.string.isRequired,
	enableScroller: PropTypes.func.isRequired,
	disableScroller: PropTypes.func.isRequired,
	buttonStyleText: PropTypes.bool,
	checkbox: PropTypes.bool,
	select: PropTypes.bool,
	dataTestId: PropTypes.string,
};

export default withEnableDisableScroller(Dropdown);
